// Geocoding service to convert addresses to lat/lng coordinates

export interface GeocodeResult {
  lat: number;
  lng: number;
  formatted_address: string;
}

export interface GeocodeError {
  error: string;
  status: string;
}

/**
 * Convert an address string to lat/lng coordinates using Google Maps Geocoding API
 * @param address - The address to geocode
 * @returns Promise with coordinates or error
 */
export const geocodeAddress = async (
  address: string
): Promise<GeocodeResult | GeocodeError> => {
  try {
    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps) {
      return { error: "Google Maps not loaded", status: "ERROR" };
    }

    const geocoder = new window.google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            formatted_address: results[0].formatted_address,
          });
        } else {
          resolve({
            error: `Geocoding failed: ${status}`,
            status: status || "UNKNOWN_ERROR",
          });
        }
      });
    });
  } catch (error) {
    return {
      error: `Geocoding error: ${error}`,
      status: "EXCEPTION",
    };
  }
};

/**
 * Batch geocode multiple addresses with delay to avoid rate limiting
 * @param addresses - Array of addresses to geocode
 * @param delayMs - Delay between requests in milliseconds (default: 200ms)
 */
export const batchGeocodeAddresses = async (
  addresses: string[],
  delayMs: number = 200
): Promise<Map<string, GeocodeResult | GeocodeError>> => {
  const results = new Map<string, GeocodeResult | GeocodeError>();

  for (const address of addresses) {
    const result = await geocodeAddress(address);
    results.set(address, result);

    // Add delay to avoid hitting rate limits
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return results;
};
